// app/javascript/controllers/ui/floating_channels_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * Floating Channels Controller
 * 
 * Простой контроллер для отображения статичных телеграм каналов
 * Только появление каналов, никакого движения
 * 
 * @usedby app/views/welcome/_floating_channels.html.erb
 * Получает данные: JSON из script tag с каналами
 * Отдает: статичные элементы каналов в контейнере
 */
export default class extends Controller {
  static targets = ["container", "channelsData", "loading"]
  static values = { 
    channelsCount: Number,
    animationSpeed: Number,
    collisionRadius: Number,
    enablePhysics: Boolean,
    enableHover: Boolean
  }

  connect() {
    console.log('[FloatingChannels] Controller connected')
    this.channels = []
    this.loadChannelsData()
  }

  disconnect() {
    console.log('[FloatingChannels] Controller disconnected')
  }

  // ===== DATA LOADING =====
  
  loadChannelsData() {
    try {
      // Получаем данные из JSON script tag
      const channelsDataElement = this.channelsDataTarget
      if (channelsDataElement) {
        const channelsData = JSON.parse(channelsDataElement.textContent)
        this.createChannels(channelsData)
      } else {
        console.warn('[FloatingChannels] No channels data found')
        this.createFallbackChannels()
      }
    } catch (error) {
      console.error('[FloatingChannels] Failed to parse channels data:', error)
      this.createFallbackChannels()
    }
  }

  createChannels(channelsData) {
    this.channels = []
    
    if (!channelsData || channelsData.length === 0) {
      this.createFallbackChannels()
      return
    }
    
    // Создаем каналы сразу без задержек
    channelsData.forEach((channelData, index) => {
      this.createChannelElement(channelData, index)
    })
    
    // Скрываем loading и добавляем класс для анимации
    this.hideLoading()
    
    // Добавляем класс для аккуратного качания после загрузки всех каналов
    setTimeout(() => {
      this.element.classList.add('floating-channels--loaded')
    }, channelsData.length * 150 + 1000)
  }

  createFallbackChannels() {
    const fallbackChannels = [
      { username: "TechNews", display_name: "Tech News", activity: "активен сегодня" },
      { username: "AIInsights", display_name: "AI Insights", activity: "новые сообщения" },
      { username: "StartupStories", display_name: "Startup Stories", activity: "онлайн сейчас" },
      { username: "DesignTrends", display_name: "Design Trends", activity: "активность" },
      { username: "MarketingTips", display_name: "Marketing Tips", activity: "обновления" },
      { username: "BusinessGrowth", display_name: "Business Growth", activity: "активные участники" }
    ]
    
    // Создаем каналы сразу без задержек
    fallbackChannels.forEach((channelData, index) => {
      this.createChannelElement(channelData, index)
    })
    
    // Скрываем loading и добавляем класс для анимации
    this.hideLoading()
    
    // Добавляем класс для аккуратного качания после загрузки всех каналов
    setTimeout(() => {
      this.element.classList.add('floating-channels--loaded')
    }, fallbackChannels.length * 150 + 1000)
  }

  createChannelElement(data, index) {
    // Создаем элемент как ссылку
    const element = document.createElement('a')
    element.className = 'channel-cloud'
    element.setAttribute('data-channel-index', index)
    
    // ✅ Используем username (полное) для ссылки, display_name (красивое) для UI
    const username = data.username || 'Unknown'
    const displayName = data.display_name || username
    const telegramUrl = `https://t.me/${username}`
    
    element.href = telegramUrl
    element.target = '_blank'
    element.rel = 'noopener noreferrer'
    element.setAttribute('aria-label', `Перейти к Telegram боту ${username}`)
    
    // Содержимое канала - отображаем красивое имя
    element.innerHTML = `
      <div class="channel-name">${displayName}</div>
      <div class="channel-activity">
        <div class="activity-dot"></div>
        <span>${data.activity || 'активность'}</span>
      </div>
    `
    
    // УМНОЕ позиционирование - избегаем наложения
    const containerRect = this.containerTarget.getBoundingClientRect()
    const elementWidth = 200
    const elementHeight = 60
    const margin = 20
    
    // Пытаемся найти свободное место
    let attempts = 0
    let x, y
    let positionFound = false
    
    while (attempts < 50 && !positionFound) {
      x = Math.random() * (containerRect.width - elementWidth - margin * 2) + margin
      y = Math.random() * (containerRect.height - elementHeight - margin * 2) + margin
      
      // Проверяем, не пересекается ли с существующими каналами
      const overlaps = this.channels.some(channel => {
        const existingRect = channel.element.getBoundingClientRect()
        const existingX = parseFloat(channel.element.style.left) || 0
        const existingY = parseFloat(channel.element.style.top) || 0
        
        return !(x + elementWidth + margin < existingX || 
                x > existingX + existingRect.width + margin ||
                y + elementHeight + margin < existingY || 
                y > existingY + existingRect.height + margin)
      })
      
      if (!overlaps) {
        positionFound = true
      }
      attempts++
    }
    
    // Если не нашли свободное место, используем сетку
    if (!positionFound) {
      const col = index % 4
      const row = Math.floor(index / 4)
      x = margin + col * (elementWidth + margin)
      y = margin + row * (elementHeight + margin)
    }
    
    element.style.position = 'absolute'
    element.style.left = `${x}px`
    element.style.top = `${y}px`
    element.style.opacity = '0'
    element.style.transform = 'scale(0.8)'
    element.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out'
    
    // Добавляем в контейнер
    this.containerTarget.appendChild(element)
    
    // Анимация появления с задержкой
    setTimeout(() => {
      element.style.opacity = '1'
      element.style.transform = 'scale(1)'
    }, index * 150)
    
    // Добавляем в массив
    this.channels.push({
      id: data.id || `channel_${index}`,
      element: element,
      data: data,
      url: telegramUrl
    })
    
    console.log(`[FloatingChannels] Channel "${displayName}" (@${username}) created at position ${x.toFixed(0)}px, ${y.toFixed(0)}px with link ${telegramUrl}`)
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.opacity = '0'
      setTimeout(() => {
        this.loadingTarget.style.display = 'none'
      }, 300)
    }
  }
}
